import dayjs from 'dayjs';
import { routeConstants } from '../constants/routeConstants';
import FIELD_TYPE from '../constants/fieldType';
import * as Yup from 'yup';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { KB } from '../constants/CommonConstants';
import { CURRENCY_CONSTANT } from '../constants/CurrencyConstants';
import { Tooltip, Typography, Box } from '@mui/material';
const { NOT_APPLICABLE } = FIELD_TYPE;
import { FEED_SOURCE_OPTIONS } from '../constants/locationConstants';

dayjs.extend(utc);

export const checkEmptyDate = (date) => {
  if ([undefined].includes(date?.toString())) {
    return null;
  } else {
    return date.toString();
  }
};

export const getControlledTooltipValueAndCellValue = (value) => {
  let cellValue;
  if (isArrayWithLength(value)) {
    cellValue = value.length > 1 ? `${value[0]}, (+${value.length - 1})` : value.join(', ');
  } else {
    cellValue = FIELD_TYPE.NOT_APPLICABLE;
  }
  return {
    tooltipValue: isArrayWithLength(value) ? value.join(', ') : null,
    cellValue,
  };
};

export const getKeyBasedObjectFromArray = (dataList, key) => {
  let keyBasedObject = {};
  dataList.forEach((data) => {
    keyBasedObject = {
      ...keyBasedObject,
      [data[key]]: data,
    };
  });
  return keyBasedObject;
};

export const getDateFormat = (date) => {
  const formateDate = dayjs.utc(date).format('MM/DD/YYYY');
  if (formateDate !== 'Invalid Date') {
    return formateDate;
  }
  return null;
};

export const getTruncatedDateFormat = (date) => {
  const dateString = date?.split('T');
  let formatDate;
  if (dateString && dateString?.length > 0) {
    formatDate = getDateFormat(dateString[0]);
  } else {
    formatDate = getDateFormat(date);
  }
  if (formatDate !== 'Invalid Date') {
    return formatDate;
  }
  return null;
};

export const getTimeFormat = (date) => {
  const formatTime = dayjs(date).format('hh:mm a');
  return formatTime;
};

export const get24HoursTimeFormat = (date) => {
  const formatTime = dayjs(date).format('HH:mm');
  if (formatTime !== 'Invalid Date') {
    return formatTime;
  } else {
    return null;
  }
};

export const formatUTCDateTimeWithAMPM = (dateString) => {
  if (!dateString) return 'N/A';

  const date = new Date(dateString);

  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  let hours = date.getUTCHours();
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  hours = hours ? hours : 12;
  const displayHours = String(hours).padStart(2, '0');

  return `${month}/${day}/${year} ${displayHours}:${minutes} ${ampm}`;
};

const truncateList = (list, limit = 3) => {
  if (!list || (Array.isArray(list) && list.length === 0)) return 'N/A';

  const items = Array.isArray(list)
    ? list.map((item) => (typeof item === 'string' ? item.trim() : item.name?.trim())).filter(Boolean)
    : list
        .toString() // Convert to string in case it's a single value
        .split(/\s*,\s*/) // Ensure proper splitting of comma-separated strings
        .map((item) => item.trim())
        .filter(Boolean);

  const sortedItems = [...items].sort((a, b) => a.localeCompare(b)); // Avoid modifying original array

  return sortedItems.length <= limit ? sortedItems.join(', ') : `${sortedItems.slice(0, limit).join(', ')}...`;
};

export const ExpandableText = ({ text, limit = 3, expanded, toggleExpand }) => {
  if (!text || (Array.isArray(text) && text.length === 0)) return <Typography variant="inherit">N/A</Typography>;

  const truncatedText = truncateList(text, limit);
  const isTruncated = truncatedText.includes('...');

  return (
    <Tooltip title={Array.isArray(text) ? text.join(', ') : text} arrow>
      <Typography
        variant="inherit"
        sx={{ cursor: isTruncated ? 'pointer' : 'default', display: 'inline', whiteSpace: 'pre-line' }}
        onClick={isTruncated ? toggleExpand : undefined}
      >
        {expanded ? (Array.isArray(text) ? text.join('\n') : text) : truncatedText}
      </Typography>
    </Tooltip>
  );
};

export const ExpandableTextWithCount = ({ text, limit = 2, expanded, toggleExpand }) => {
  if (!text || (Array.isArray(text) && text.length === 0)) return <Typography variant="inherit">N/A</Typography>;

  const items = Array.isArray(text)
    ? text.map((item) => (typeof item === 'string' ? item.trim() : item.name?.trim())).filter(Boolean)
    : text
        .toString()
        .split(/\s*,\s*/)
        .map((item) => item.trim())
        .filter(Boolean);

  const sortedItems = [...items].sort((a, b) => a.localeCompare(b));

  if (sortedItems.length <= limit) {
    return (
      <Typography variant="inherit" sx={{ whiteSpace: 'pre-line' }}>
        {sortedItems.join(',\n')}
      </Typography>
    );
  }

  const displayItems = expanded ? sortedItems : sortedItems.slice(0, limit);
  const remainingCount = sortedItems.length - limit;

  return (
    <Box
      sx={{
        cursor: 'pointer',
      }}
      onClick={toggleExpand}
    >
      <Typography variant="inherit" component="span" sx={{ whiteSpace: 'pre-line' }}>
        {displayItems.join(',\n')}
      </Typography>
      {!expanded && remainingCount > 0 && (
        <Typography
          variant="inherit"
          component="span"
          sx={{
            color: '#577188',
            fontWeight: 500,
            ml: 0.5,
          }}
        >
          {` (+${remainingCount})`}
        </Typography>
      )}
    </Box>
  );
};

export const set24HoursTimeFormat = (hourAndMin) => {
  if (!hourAndMin || hourAndMin === NOT_APPLICABLE) return null;
  const hour = hourAndMin?.split(':')[0] || 0;
  const min = hourAndMin?.split(':')[1] || 0;
  const sec = hourAndMin?.split(':')[2] || 0;
  if (typeof hour === 'string' && isNaN(hour)) return null;
  if (typeof min === 'string' && isNaN(min)) return null;
  if (typeof sec === 'string' && isNaN(sec)) return null;
  const formatTime = dayjs()
    .set('hour', hour || 0)
    .set('minute', min || 0)
    .set('second', sec || 0);
  return formatTime;
};

export const getTimeFormatForStaticView = (time) => {
  const mergeTimeWithCurrentDate = `${dayjs().format('YYYY-MM-DD')} ${time}`;
  const getExactTime = dayjs(mergeTimeWithCurrentDate).format('hh:mma');
  return getExactTime;
};

export const getTimeFormatForFormView = (time) => {
  const mergeTimeWithCurrentDate = `${dayjs().format('YYYY-MM-DD')} ${time}`;
  return mergeTimeWithCurrentDate.toString();
};

export const getDisplayTime = (timeValue) => {
  if (!isValueValid(timeValue) || (typeof timeValue === 'string' && timeValue.includes('null'))) {
    return undefined;
  }

  const parsedDateTime = dayjs(timeValue);
  if (parsedDateTime.isValid()) {
    return parsedDateTime.format('hh:mma');
  }

  if (typeof timeValue === 'string') {
    return getTimeFormatForStaticView(timeValue);
  }

  return undefined;
};

export const dateFormatForApi = (date) => {
  const formateDate = dayjs(date).format('YYYY-MM-DD');
  if (formateDate !== 'Invalid Date') {
    return formateDate;
  } else {
    return null;
  }
};

export const formatDateForApiSafely = (dateValue) => {
  if (!dateValue) return null;

  if (typeof dateValue === 'string') {
    const matched = dateValue.match(/^(\d{4}-\d{2}-\d{2})/);
    if (matched?.[1]) {
      return matched[1];
    }
  }

  const parsedDate = dayjs(dateValue);
  return parsedDate.isValid() ? parsedDate.format('YYYY-MM-DD') : null;
};

export const isBothArraySame = (array1 = [], array2 = [], key) => {
  const array2Sorted = array2.slice().sort();
  return (
    array1.length === array2.length &&
    array1
      .slice()
      .sort()
      .every(function (value, index) {
        return value[key] === array2Sorted[index][key];
      })
  );
};

export const isArrayWithLength = (arr) => Array.isArray(arr) && arr.length > 0;

export const getUniqueSubLocations = (list = []) => {
  const seen = new Set();
  return (list || []).filter((subLocation) => {
    const id = subLocation?.id;
    if (!id || seen.has(id)) return false;
    seen.add(id);
    return true;
  });
};

export const checkNAOrReturnPercentValue = (value) => {
  if (isNaN(value)) return 'N/A';
  if (value === 0) return '0%';

  const multiplied = value * 100;
  return Number.isInteger(multiplied) ? `${multiplied}%` : `${multiplied.toFixed(2).replace(/\.?0+$/, '')}%`;
};

export const formatTaxPercentValue = (value) => {
  if (isNaN(value)) return 'N/A';
  if (value === 0) return '0%';

  const multiplied = value * 100;
  return Number.isInteger(multiplied) ? `${multiplied}%` : `${multiplied.toFixed(4).replace(/\.?0+$/, '')}%`;
};

export const formatNumberOrReturnZero = (value) =>
  checkValueNotNullUndefinedBlank(value) ? Number(value).toFixed(2) : '0.00';

export const intersection = (arrA, arrB) => arrA.filter((x) => arrB.includes(x));

export const checkValueNotNullUndefinedBlank = (value) =>
  value === null || value === undefined || value === '' ? false : true;

export const getOptions = (data, keyName, includeDisable = false) => {
  if (!data) {
    return [];
  }
  const key = keyName || 'name';
  return data?.map((value) => ({
    id: value?.id,
    name: value?.[key]?.trim() || value?.title?.trim(),
    text: value?.[key]?.trim() || value?.title?.trim(),
    value: value?.id,
    disabled: includeDisable ? value.status : false,
  }));
};
export const setPlayCardSummaryValToDecimal = (obj) => {
  // Check if the current item is an object and format its properties recursively
  if (typeof obj === 'object' && obj !== null) {
    return Object.fromEntries(
      Object.entries(obj).map(([key, value]) => {
        if (typeof value === 'object' && value !== null) {
          return [key, setPlayCardSummaryValToDecimal(value)]; // Recursively format nested objects
        }
        if (typeof value === 'number') {
          return [key, value.toFixed(2)];
        }
        if (!isNaN(value) && value !== '') {
          return [key, parseFloat(value).toFixed(2)];
        }
        return [key, value];
      }),
    );
  }
  return obj; // Return as-is if not an object
};

// Function to format values in accountSummaryState
export const FormatAccountSummaryState = (state) =>
  state.map((account) => ({
    ...account,
    debit:
      checkValueNotNullUndefinedBlank(account.debit) && account.debit > 0
        ? parseFloat(account.debit).toFixed(2)
        : '0.00',
    credit:
      checkValueNotNullUndefinedBlank(account.credit) && account.credit > 0
        ? parseFloat(account.credit).toFixed(2)
        : '0.00',
  }));

export const getCurrency = (value) => {
  switch (value) {
    case CURRENCY_CONSTANT.USD:
    case CURRENCY_CONSTANT.BMD:
    case CURRENCY_CONSTANT.HKD:
    case CURRENCY_CONSTANT.AUD:
    case CURRENCY_CONSTANT.CAD:
      return '$';
    case CURRENCY_CONSTANT.INR:
      return '₹';
    case CURRENCY_CONSTANT.EUR:
      return '€';
    case CURRENCY_CONSTANT.CNY:
      return '¥';
    case CURRENCY_CONSTANT.THB:
      return '฿';
    case CURRENCY_CONSTANT.GBP:
    case CURRENCY_CONSTANT.GIP:
      return '£';
    default:
      return '$';
  }
};

/**
 * Format a numeric value as currency using Intl.NumberFormat
 * @param {number} value - The numeric value to format
 * @param {Object} options - Formatting options
 * @param {string} options.currency - Currency code (default: 'USD')
 * @param {number} options.minimumFractionDigits - Minimum decimal places (default: 0)
 * @param {number} options.maximumFractionDigits - Maximum decimal places (default: 0)
 * @param {string} options.fallback - Value to return if input is null/undefined (default: '$0')
 * @returns {string} Formatted currency string
 * @example
 * formatCurrencyValue(1234.56) // returns "$1,235"
 * formatCurrencyValue(1234.56, { maximumFractionDigits: 2 }) // returns "$1,234.56"
 * formatCurrencyValue(null) // returns "$0"
 */
export const formatCurrencyValue = (value, options = {}) => {
  const { currency = 'USD', minimumFractionDigits = 0, maximumFractionDigits = 0, fallback = '$0' } = options;

  if (value === null || value === undefined) return fallback;

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(value);
};

export const removeItemOnceFromArray = (arr, value) => {
  const index = arr.indexOf(value);
  if (index > -1) {
    arr.splice(index, 1);
  }
  return arr;
};

export const isBothObjectSame = (obj1, obj2) => {
  if (!isBothArraySame(Object.keys(obj1), Object.keys(obj2))) return false;
  let isSame = true;
  for (const key in obj1) {
    if (obj1[key] !== obj2[key]) {
      isSame = false;
    }
  }
  return isSame;
};

export const removeKeysFromObj = (obj = {}, keys = []) => {
  keys.forEach((key) => {
    delete obj[key];
  });
  return obj;
};

export const getPhNo10Digits = (phString = null, n = 10) => {
  if (!phString) return;
  phString = phString.replace(/\D/g, '');
  return phString.slice(phString.length - n);
};

export const getPhNo10DigitsWithCountryCode = (phString = null) => {
  if (!phString) return;
  if (phString.includes('+')) {
    let phArr = phString.split(' ');
    phArr[1] = getPhNo10Digits(phArr[1]);
    return phArr.join(' ');
  }
  return getPhNo10Digits(phString);
};

export const formatPhNo10Digit = (phString = null) => {
  //format to DDD-DDD-DDDD
  if (!phString || phString === NOT_APPLICABLE) return null;
  phString = phString.includes('+') ? getPhNo10DigitsWithCountryCode(phString) : getPhNo10Digits(phString);
  //string formation from last because the last 10 digits will always be valid phone number without country code
  const last4dig = phString.slice(phString.length - 4);
  const middle3dig = phString.slice(phString.length - 7, phString.length - 4);
  const first3dig = phString.slice(phString.length - 10, phString.length - 7);
  const countryCode = phString.slice(0, phString.length - 10);
  return `${countryCode}${first3dig}-${middle3dig}-${last4dig}`;
};

export const mergedTwoPhoneNumber = (phone1, phone2) => {
  const formatPhone1 = formatPhNo10Digit(phone1);
  const formatPhone2 = formatPhNo10Digit(phone2);
  if (phone1 && phone2) {
    return `${formatPhone1}, ${formatPhone2}`;
  } else if (phone1) {
    return formatPhone1;
  } else if (phone2) {
    return formatPhone2;
  } else {
    return 'N/A';
  }
};

export const getUsersMergeString = (users) => {
  const names = users?.map((value) => `${value?.firstName} ${value?.lastName}`).join(', ');
  return names;
};

export const getParentRoutePath = (path) => {
  const {
    USER_ROUTE,
    USERS_ROUTE,
    LOCATION_ROUTE,
    COLLECTIONS_ROUTE,
    MACHINES_ROUTE,
    MODELS_ROUTE,
    MODEMS_ROUTE,
    LOCATION_OWNERS_ROUTE,
    ADD_NEW_LOCATION_OWNER,
    LEASE_ROUTE,
    SETTING_ROUTE,
  } = routeConstants;

  if (path.includes(SETTING_ROUTE)) {
    return '/' + SETTING_ROUTE;
  }

  if (path.includes(USER_ROUTE)) {
    return '/' + USERS_ROUTE;
  }

  if (path.includes(LOCATION_OWNERS_ROUTE) || path.includes(ADD_NEW_LOCATION_OWNER)) {
    return '/' + LOCATION_OWNERS_ROUTE;
  }

  if (path.includes(LOCATION_ROUTE)) {
    return '/' + LOCATION_ROUTE;
  }

  if (path.includes(COLLECTIONS_ROUTE)) {
    return '/' + COLLECTIONS_ROUTE;
  }
  if (path.includes(LEASE_ROUTE)) {
    return '/' + LEASE_ROUTE;
  }

  const parentRoutes = [MACHINES_ROUTE, MODELS_ROUTE, MODEMS_ROUTE];
  const res = parentRoutes.find((route) => path.includes(route));
  if (res) return '/' + res;
  return path;
};

export const debounce = (func, delay = 600) => {
  let timer;
  return function (...args) {
    const context = this;
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      func.apply(context, args);
    }, delay);
  };
};

export const isValueValid = (value) => value !== null && value !== undefined;

export const isAllObjValuesNullOrEmpty = (obj) =>
  Object.values(obj).every((x) => x === null || parseInt(x) === 0 || x === '');

export const isAllObjectValuesNullOrZero = (obj) => Object.values(obj).every((x) => x === null || parseInt(x) === 0);

export const checkNumberIsNaNAndInfinity = (value = 0) => {
  if (typeof value !== 'number') {
    const transformedValue = Number(value);
    return isNaN(transformedValue) ? 0 : isFinite(transformedValue) ? transformedValue : 0;
  }
  return isNaN(value) ? 0 : isFinite(value) ? value : 0;
};

/**
 *
 * @param {Object} scheme
 * @param {Array} dependencies
 * @returns Yup schema object
 */
export const getValidationSchemeYupObject = (scheme, dependencies) =>
  Yup.object().shape({ ...scheme }, [...dependencies]);

export const getAddress = (location) => {
  const address = location?.address1 ?? 'N/A';
  const arr = [];
  arr.push(address);
  const address2 = location?.address2;
  const address3 = location?.address3;
  if (address2) {
    arr.push(address2);
  }
  if (address3) {
    arr.push(address3);
  }
  if (arr.length > 1) {
    return arr.join(', ');
  }
  return address;
};

export const getESTDateFormat = (date) => {
  dayjs.extend(utc);
  dayjs.extend(timezone);
  dayjs.tz.setDefault('America/New_York');
  const newDate = dayjs(Number(date)).tz().format('MM-DD-YYYY HH:mm');
  return newDate;
};

/**
 * Round value to 2 digits by default, return 0 if value is NaN or undefined
 *
 * @param {*} value
 * @param {*} digits
 * @param {*} setInteger
 * @returns
 */
export const roundOffValue = (value, digits, setInteger = false) => {
  if (setInteger && Number.isInteger(value)) {
    return value;
  }
  if (digits !== null && digits !== undefined && !isNaN(digits)) {
    return Number(checkNumberIsNaNAndInfinity(value).toFixed(checkNumberIsNaNAndInfinity(digits)));
  }
  return Number(checkNumberIsNaNAndInfinity(value).toFixed(2));
};

export const getUsedLocalStorageSpaceSize = () => {
  let data = '';
  for (const key in window.localStorage) {
    if (Object.prototype.hasOwnProperty.call(window.localStorage, key)) {
      data += window.localStorage[key];
    }
  }
  const size = (checkNumberIsNaNAndInfinity(data?.length) * 16) / (8 * KB);
  return size;
};

export const getFormDataSize = (formData) => {
  let data = '';
  for (const key in formData) {
    if (key in formData) {
      data += formData[key];
    }
  }
  const size = (checkNumberIsNaNAndInfinity(data?.length) * 16) / (8 * KB);
  return size;
};

export const isExceedLocalStorageLimit = (limit, usedSpaceSize) => {
  const remainSpaceSize = checkNumberIsNaNAndInfinity(limit) * KB - checkNumberIsNaNAndInfinity(usedSpaceSize);
  if (remainSpaceSize <= 0) {
    return true;
  }
  return false;
};

export const sortArrayOfObjects = (array = [], property, direction) => {
  direction = direction || 1;
  const sortArray = [...array];
  sortArray?.sort(function compare(a, b) {
    let comparison = 0;
    if (a[property] > b[property]) {
      comparison = 1 * direction;
    } else if (a[property] < b[property]) {
      comparison = -1 * direction;
    }
    return comparison;
  });
  return sortArray;
};

export const checkObjectNotEmpty = (obj) => {
  for (let i in obj) return true;
  return false;
};

export const rentTypeString = (type) => {
  switch (type) {
    case 'FEE_PLUS_RATE':
      return 'Fee Plus Rate';
    case 'FEE_ONLY':
      return 'Fee Only';
    case 'RATE_ONLY':
      return 'Rate Only';
    case 'FEE_PLUS_RATE_COMBO':
      return 'Fee Plus Rate Combo';
    case 'BREAKPOINT_RATE_TERM':
      return 'Breakpoint Rate - Term';
    case 'BREAKPOINT_RATE_TERM_COMBO':
      return 'Breakpoint Rate - Term Combo';
    case 'FEE_TERM_COMBO':
      return 'Fee - Term Combo';
    case 'FEE_PLUS_BREAKPOINT_RATE_MONTHLY':
      return 'Fee Plus Breakpoint Rate - Monthly';
    case 'FEE_PLUS_BREAKPOINT_RATE_TERM':
      return 'Fee Plus Breakpoint Rate - Term';
    case 'FEE_PLUS_BREAKPOINT_RATE_MONTHLY_COMBO':
      return 'Fee Plus Breakpoint Rate - Monthly Combo';
    case 'FEE_PLUS_BREAKPOINT_RATE_TERM_COMBO':
      return 'Fee Plus Breakpoint Rate - Term Combo';
    case 'RATE_ONLY_TIERED':
      return 'Rate Only Tiered';
    case 'RATE_ONLY_TIERED_TERM_COMBO':
      return 'Rate Only Tiered - Term Combo';
    case 'FEE_PLUS_TIERED_RATE':
      return 'Fee Plus Tiered Rate';
    case 'RATE_PLUS_BREAKPOINT_RATE_TERM':
      return 'Rate Plus Breakpoint Rate - Term';
    default:
      return 'N/A';
  }
};

export const isRentTypeInGroup = (rentType, group) => group.includes(rentType);

export const eachWordFirstLetterCapital = (str) => {
  const arr = str.split(' ');
  for (var i = 0; i < arr.length; i++) {
    arr[i] = arr[i].charAt(0).toUpperCase() + arr[i].slice(1);
  }
  const str2 = arr.join(' ');
  return str2;
};

/**
 * Calculate difference between subtrahend and minuend
 *
 * @param {any} subtrahend
 * @param {any} minuend
 * @returns
 */
export const calculateDifferenceBetweenTwoFields = (subtrahend, minuend) =>
  Math.abs(checkNumberIsNaNAndInfinity(subtrahend) - checkNumberIsNaNAndInfinity(minuend));

export const handledSpecialCharacterRegex = (str) => {
  const modifyPatter = str
    .split('')
    .map((value) => {
      let pattern = new RegExp('[a-zA-Z0-9]', 'i');
      if (!pattern.test(value)) {
        return `\\${value}`;
      }
      return value;
    })
    .join('');
  return modifyPatter;
};

export const timeCalculation = (expireTime) => {
  const MILLISECONDS_MINUTES_FORMULA = 60000;

  const currentTime = new Date().getTime();
  if (expireTime) {
    const getTimeDifference = expireTime - currentTime;
    const reducedTimeInMinutes = 5; // reduced 5 minutes of actual token time.
    const tokenReducedTime = reducedTimeInMinutes * MILLISECONDS_MINUTES_FORMULA;
    const finalRemainingTime = getTimeDifference - tokenReducedTime;
    return finalRemainingTime;
  }
  return 0;
};

export const replaceStartValueZeroWithNumber = (value) =>
  value.includes('-') ? value : value.replace(/^0+(?=\d+(\.\d+)?)/, '');

export const getDateWithoutConversion = (date) => {
  let formattedDate = date;
  if (formattedDate) {
    const arr = formattedDate?.split('');
    formattedDate = arr?.slice(0, arr?.length - 1)?.join('');
    formattedDate = getDateFormat(formattedDate);
  }
  return formattedDate;
};

export const getFullName = (firstName, lastName) => {
  const finalName =
    firstName && lastName
      ? `${firstName} ${lastName}`
      : firstName && !lastName
      ? firstName
      : !firstName && lastName
      ? lastName
      : 'N/A';
  return finalName;
};

export const checkIsNegativeZero = (value) => 1 / value === -Infinity;

export const getUserName = (userDetail) => {
  if (userDetail?.firstName) {
    return `${userDetail?.firstName} ${userDetail?.lastName}`;
  }
  return '-';
};

export const getSum = (a, b) => {
  const value1 = checkNumberIsNaNAndInfinity(a);
  const value2 = checkNumberIsNaNAndInfinity(b);
  return parseFloat((value1 + value2).toFixed(10));
};

export const setActiveTab = (tabId) => {
  localStorage.setItem('ACTIVE_TAB', tabId);
};

export const getActiveTab = () => localStorage.getItem('ACTIVE_TAB');

export const getTabList = () => (localStorage.getItem('TAB_LIST') ? JSON.parse(localStorage.getItem('TAB_LIST')) : []);
export const addToTabList = (tabId) => {
  const tabList = getTabList();
  tabList.push(tabId);
  localStorage.setItem('TAB_LIST', JSON.stringify(tabList));
};

export const removeFromTabList = (tabId) => {
  const tabList = getTabList();
  const doesInclude = tabList.includes(tabId);
  if (tabList.length && doesInclude) {
    localStorage.setItem('TAB_LIST', JSON.stringify(tabList.filter((id) => id != tabId)));
  }
};

export const getMinInEffectFromDate = () => {
  const today = dayjs();
  const firstOfCurrentMonth = today.startOf('month');
  return firstOfCurrentMonth;
};

/**
 * Snakekase
 * @param {string} input - The input string to convert
 * @returns {string} - The converted string in Title Case
 * @example
 * snakeToTitleCase('LONG_TERM_RENT'); // returns "Long Term Rent"
 * snakeToTitleCase('long-term-rent'); // returns "Long Term Rent"
 */
export const snakeToTitleCase = (input) => {
  if (input == null) return '';
  const s = String(input).trim();
  if (!s) return '';

  return s
    .replace(/[_-]+/g, ' ')
    .trim()
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
};
export const parseFeedSourceValues = (feedSourceString) => {
  if (!feedSourceString) return [];
  if (Array.isArray(feedSourceString)) return feedSourceString;
  const feedSourceArray = feedSourceString.split(',').map((item) => item.trim());
  return FEED_SOURCE_OPTIONS.filter((option) => feedSourceArray.includes(option.value));
};

export const formatCurrencyUSD = (value) => {
  const numValue = typeof value === 'number' ? value : parseFloat(value) || 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numValue);
};

export const calculateDebitCreditTotals = (data) => {
  const safeData = Array.isArray(data) ? data : [];
  const totalDebit = safeData.reduce((sum, row) => sum + (parseFloat(row?.debit) || 0), 0);
  const totalCredit = safeData.reduce((sum, row) => sum + (parseFloat(row?.credit) || 0), 0);
  const isBalanced = Math.abs(totalDebit - totalCredit) < 0.01;
  return { totalDebit, totalCredit, isBalanced };
};
